/** Headers the API reads to verify a call has not been replayed or edited. */
export type SignatureHeaders = {
  'X-Ttt-Nonce': string;
  'X-Ttt-Ts': string;
  'X-Ttt-Sig': string;
};

const toBase64Url = (buffer: ArrayBuffer): string =>
  // Array.from rather than a spread: the project targets es5, where spreading a typed array needs
  // downlevelIteration. The buffers here are 16 and 32 bytes, well within apply's limits.
  btoa(String.fromCharCode(...Array.from(new Uint8Array(buffer))))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const toHex = (buffer: ArrayBuffer): string =>
  Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');

/**
 * @description Signs a request so the API can tell it apart from a replay.
 *
 * The signature covers the nonce, the timestamp, the method, the full path and a hash of the body:
 * a captured request cannot be sent twice (the API consumes the nonce), moved to another endpoint,
 * or have its parameters edited. The session token doubles as the signing key - the API already
 * receives it, so nothing extra travels.
 *
 * @param {string} token The session token, used as the signing key
 * @param {string} url The URL being requested
 * @param {RequestInit} [options] The options passed to "fetch"
 * @param {number} [clockOffset] Seconds to add to the local clock to match the server's
 * @returns {Promise<SignatureHeaders | null>} The headers to add, or null when signing is unavailable
 */
export default async function signRequest(
  token: string,
  url: string,
  options?: RequestInit,
  clockOffset = 0,
): Promise<SignatureHeaders | null> {
  // Web Crypto needs a secure context, and TextEncoder is absent from some test environments.
  // Built here rather than at module scope so importing this file can never throw.
  if (!window.crypto?.subtle || typeof TextEncoder === 'undefined') {
    return null;
  }

  try {
    const encoder = new TextEncoder();
    const target = new URL(url, window.location.origin);
    const path = `${target.pathname}${target.search}`;
    const method = (options?.method || 'GET').toUpperCase();
    const body = typeof options?.body === 'string' ? options.body : '';

    const nonce =
      window.crypto.randomUUID?.() ||
      toBase64Url(window.crypto.getRandomValues(new Uint8Array(16)).buffer);
    const timestamp = String(Math.floor(Date.now() / 1000) + clockOffset);

    const bodyHash = toHex(
      await window.crypto.subtle.digest('SHA-256', encoder.encode(body)),
    );
    const payload = [nonce, timestamp, method, path, bodyHash].join('.');

    const key = await window.crypto.subtle.importKey(
      'raw',
      encoder.encode(token),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign'],
    );
    const signature = await window.crypto.subtle.sign(
      'HMAC',
      key,
      encoder.encode(payload),
    );

    return {
      'X-Ttt-Nonce': nonce,
      'X-Ttt-Ts': timestamp,
      'X-Ttt-Sig': toBase64Url(signature),
    };
  } catch (error) {
    return null;
  }
}
